import { CalendarClock, CalendarPlus, Github, Users } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import type { HomeworkItem } from '@/types/homework';
import { dueLabel, formatInstituteDate, isFullyHandedIn } from '../../utils';

/**
 * The facts about one piece of work: subject, when it was set, when it is due,
 * and how many have handed in.
 *
 * Shared by the list card and the top of the review screen, which are the two
 * places a teacher reads it. Written once so the two cannot start disagreeing
 * about what "overdue" looks like or how a date is spelled (CLAUDE.md §3).
 */

export interface HomeworkMetaProps {
  homework: HomeworkItem;
  /** `YYYY-MM-DD`, from the server. Never `new Date()` — see `dueLabel`. */
  today: string;
  className?: string;
}

export function HomeworkMeta({ homework, today, className }: HomeworkMetaProps) {
  const due = dueLabel(homework.dueDate, today);
  const isComplete = isFullyHandedIn(homework);

  return (
    <div
      className={cn('flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-2', className)}
    >
      <Badge tone="accent">{homework.subject.name}</Badge>

      <span className="flex items-center gap-1.5">
        <CalendarPlus className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
        Set {formatInstituteDate(homework.assignedDate)}
      </span>

      <span
        className={cn(
          'flex items-center gap-1.5 font-[560]',
          due.isOverdue ? 'text-crit' : 'text-warn',
        )}
      >
        <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {due.text}
      </span>

      <span className="flex items-center gap-1.5">
        <Users className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
        <span className={cn('font-[560]', isComplete ? 'text-ok' : 'text-ink')}>
          {homework.submissionCount}
        </span>
        <span className="text-ink-3">/ {homework.totalStudents} handed in</span>
      </span>

      {homework.maxMarks !== null && <span className="text-ink-3">Out of {homework.maxMarks}</span>}

      {/* Only when it is the unusual one. Saying "a file" on every piece of
          work in the product would be a badge nobody reads. */}
      {homework.submissionFormat === 'REPO_URL' && (
        <span className="flex items-center gap-1.5 text-ink-3">
          <Github className="h-3.5 w-3.5 shrink-0" aria-hidden />
          Handed in as a repository link
        </span>
      )}
    </div>
  );
}
